/* eslint-disable max-params */
import type {
  DurableChatTurnAcceptedResponse,
  DurableChatTurnAcceptRequest,
  DurableChatTurnCancelRequest,
  DurableChatTurnCancelResponse,
  DurableChatTurnControlRequest,
  DurableChatTurnControlResponse,
  DurableChatTurnCurrentResponse,
  DurableChatTurnStatusResponse,
  DurableChatTurnSteerRequest,
  DurableChatTurnSteerResponse,
} from "../models"

import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

// Must match `SESSION_REVISION_HEADER` in app/backend/routes/chat_turns.py.
// Also needs `expose_headers` in app.py's dev CORS config, or a cross-origin
// dev build can't read it off the response at all.
const SESSION_REVISION_HEADER = "X-Chat-Turn-Revision"

function turnsUrl(sessionId: string): string {
  return `${BACKEND_URI}/chat-sessions/${encodeURIComponent(sessionId)}/turns`
}

async function readJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T
}

export async function acceptDurableChatTurnApi(
  sessionId: string,
  payload: DurableChatTurnAcceptRequest,
  idToken?: string
): Promise<DurableChatTurnAcceptedResponse> {
  const canonicalPayload: DurableChatTurnAcceptRequest = {
    ...payload,
    request: {
      ...payload.request,
      turn_id: payload.turnId,
    },
  }
  const response = await fetchWithAuthRetry(
    turnsUrl(sessionId),
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(canonicalPayload),
    },
    idToken
  )

  return readJson<DurableChatTurnAcceptedResponse>(response)
}

export async function getCurrentDurableChatTurnApi(
  sessionId: string,
  idToken?: string,
  // Optional so every existing caller (the save-guard watchdog, the one-shot
  // hydration read) is untouched by this. Only the recurring discovery poll
  // (issue #2513, finding 18/D) needs the revision, to tell "nothing changed
  // since I last synced" from "go refetch the message history" without a
  // second round trip just to find out.
  onRevision?: (revision: string | null) => void
): Promise<DurableChatTurnCurrentResponse | null> {
  const response = await fetchWithAuthRetry(
    `${turnsUrl(sessionId)}/current`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  onRevision?.(response.headers.get(SESSION_REVISION_HEADER))

  if (response.status === 204) return null
  return readJson<DurableChatTurnCurrentResponse>(response)
}

export async function getDurableChatTurnStatusApi(
  sessionId: string,
  turnId: string,
  afterSeq: number,
  idToken?: string
): Promise<DurableChatTurnStatusResponse> {
  if (!Number.isSafeInteger(afterSeq) || afterSeq < 0) {
    throw new RangeError("afterSeq must be a non-negative safe integer")
  }

  const response = await fetchWithAuthRetry(
    `${turnsUrl(sessionId)}/${encodeURIComponent(turnId)}?afterSeq=${afterSeq}`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )

  return readJson<DurableChatTurnStatusResponse>(response)
}

export async function cancelDurableChatTurnApi(
  sessionId: string,
  payload: DurableChatTurnCancelRequest,
  idToken?: string
): Promise<DurableChatTurnCancelResponse> {
  const response = await fetchWithAuthRetry(
    `${turnsUrl(sessionId)}/${encodeURIComponent(payload.turnId)}/cancel`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(payload),
    },
    idToken
  )

  return readJson<DurableChatTurnCancelResponse>(response)
}

/** Post one mid-run steer note to a running durable turn (#2814).
 *
 *  The durable counterpart of the WebSocket `steer` frame. The turn's outcome
 *  for this note arrives asynchronously as a `steer_status` replay event, not
 *  in this response — `accepted` here only means the note was durably stored.
 */
export async function sendDurableChatTurnSteerNoteApi(
  sessionId: string,
  payload: DurableChatTurnSteerRequest,
  idToken?: string
): Promise<DurableChatTurnSteerResponse> {
  const response = await fetchWithAuthRetry(
    `${turnsUrl(sessionId)}/${encodeURIComponent(payload.turnId)}/steer`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({
        seq: payload.seq,
        content: payload.content,
        steerId: payload.steerId,
      }),
    },
    idToken
  )

  return readJson<DurableChatTurnSteerResponse>(response)
}

export async function sendDurableChatTurnControlApi(
  sessionId: string,
  payload: DurableChatTurnControlRequest,
  idToken?: string
): Promise<DurableChatTurnControlResponse> {
  const response = await fetchWithAuthRetry(
    `${turnsUrl(sessionId)}/${encodeURIComponent(payload.turnId)}/controls`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(payload),
    },
    idToken
  )

  return readJson<DurableChatTurnControlResponse>(response)
}
